import * as React from 'react';
import { SearchIcon } from 'lucide-react';

import { cn } from '@cotera/client/v0/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@cotera/client/v0/components/ui/dialog';

// Custom Command Context
interface CommandContextValue {
  search: string;
  setSearch: (search: string) => void;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  items: Array<{ id: string; element: HTMLElement; disabled?: boolean }>;
  registerItem: (id: string, element: HTMLElement, disabled?: boolean) => void;
  unregisterItem: (id: string) => void;
  onSelect?: (value: string) => void;
}

const CommandContext = React.createContext<CommandContextValue | null>(null);

function useCommand() {
  const context = React.useContext(CommandContext);
  if (!context) {
    throw new Error('Command components must be used within Command');
  }
  return context;
}

function Command({
  className,
  onValueChange,
  value,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  onValueChange?: (search: string) => void;
  value?: string;
}) {
  const [search, setSearch] = React.useState(value || '');
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const [items, setItems] = React.useState<
    Array<{ id: string; element: HTMLElement; disabled?: boolean }>
  >([]);

  const registerItem = React.useCallback(
    (id: string, element: HTMLElement, disabled?: boolean) => {
      setItems((prev) => {
        const existing = prev.find((item) => item.id === id);
        if (existing) {
          return prev;
        }
        return [...prev, { id, element, disabled }];
      });
    },
    []
  );

  const unregisterItem = React.useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleSearchChange = React.useCallback(
    (newSearch: string) => {
      setSearch(newSearch);
      onValueChange?.(newSearch);
      setSelectedIndex(-1);
    },
    [onValueChange]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      const visibleItems = items.filter(
        (item) => !item.disabled && item.element.offsetParent !== null
      );

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => {
          const next = prev + 1;
          return next >= visibleItems.length ? 0 : next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => {
          const next = prev - 1;
          return next < 0 ? visibleItems.length - 1 : next;
        });
      } else if (
        e.key === 'Enter' &&
        selectedIndex >= 0 &&
        selectedIndex < visibleItems.length
      ) {
        e.preventDefault();
        const selectedItem = visibleItems[selectedIndex];
        selectedItem?.element.click();
      } else if (e.key === 'Escape') {
        setSelectedIndex(-1);
      }
    },
    [items, selectedIndex]
  );

  React.useEffect(() => {
    if (value !== undefined) {
      setSearch(value);
    }
  }, [value]);

  const contextValue: CommandContextValue = {
    search,
    setSearch: handleSearchChange,
    selectedIndex,
    setSelectedIndex,
    items,
    registerItem,
    unregisterItem,
  };

  return (
    <CommandContext.Provider value={contextValue}>
      <div
        data-slot="command"
        className={cn(
          'bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md',
          className
        )}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {children}
      </div>
    </CommandContext.Provider>
  );
}

function CommandDialog({
  title = 'Command Palette',
  description = 'Search for a command to run...',
  children,
  className,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
}) {
  return (
    <Dialog {...props}>
      <DialogHeader className="sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        className={cn('overflow-hidden p-0', className)}
        showCloseButton={showCloseButton}
      >
        <Command className="[&_[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function CommandInput({
  className,
  placeholder = 'Type a command or search...',
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  const { search, setSearch } = useCommand();

  return (
    <div
      data-slot="command-input-wrapper"
      className="flex h-9 items-center gap-2 border-b border-foreground/10 px-3"
    >
      <SearchIcon className="size-4 shrink-0 opacity-50" />
      <input
        data-slot="command-input"
        className={cn(
          'placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={placeholder}
        {...props}
      />
    </div>
  );
}

function CommandList({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="command-list"
      className={cn(
        'max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function CommandEmpty({
  children = 'No results found.',
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { search, items } = useCommand();

  // Check if there are any visible items
  const hasVisibleItems = React.useMemo(() => {
    if (!search) {
      return items.length > 0;
    }

    return items.some((item) => {
      const text = item.element.textContent?.toLowerCase() || '';
      return (
        text.includes(search.toLowerCase()) &&
        item.element.offsetParent !== null
      );
    });
  }, [search, items]);

  if (hasVisibleItems) {
    return null;
  }

  return (
    <div
      data-slot="command-empty"
      className={cn('py-6 text-center text-sm', className)}
      {...props}
    >
      {children}
    </div>
  );
}

function CommandGroup({
  className,
  heading,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  heading?: string;
}) {
  const { search } = useCommand();
  const groupRef = React.useRef<HTMLDivElement>(null);

  // Filter children based on search
  const filteredChildren = React.useMemo(() => {
    if (!search) {
      return children;
    }

    const query = search.toLowerCase();
    return React.Children.toArray(children).filter((child) => {
      if (!React.isValidElement(child)) {
        return false;
      }

      // Mirror CommandItem's own visibility check: prefer its `value`, fall
      // back to string children, and keep anything we can't read. This keeps
      // the group heading in sync with which items are actually visible (so an
      // empty group's heading disappears during search).
      const value = child.props.value;
      const text =
        typeof value === 'string'
          ? value
          : typeof child.props.children === 'string'
          ? child.props.children
          : null;
      if (text === null) {
        return true;
      }
      return text.toLowerCase().includes(query);
    });
  }, [children, search]);

  const hasVisibleChildren = React.Children.count(filteredChildren) > 0;

  if (!hasVisibleChildren) {
    return null;
  }

  return (
    <div
      ref={groupRef}
      data-slot="command-group"
      className={cn(
        'text-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
        className
      )}
      {...props}
    >
      {heading && (
        <div
          data-slot="command-group-heading"
          className="px-2 py-1.5 text-xs font-medium text-muted-foreground/60"
        >
          {heading}
        </div>
      )}
      {filteredChildren}
    </div>
  );
}

function CommandSeparator({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="command-separator"
      className={cn('bg-border -mx-1 h-px', className)}
      {...props}
    />
  );
}

function CommandItem({
  className,
  children,
  onSelect,
  disabled,
  value,
  ...props
}: Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> & {
  onSelect?: (value: string) => void;
  disabled?: boolean;
  value?: string;
}) {
  const { search, selectedIndex, items, registerItem, unregisterItem } =
    useCommand();
  const itemRef = React.useRef<HTMLDivElement>(null);
  const itemId = React.useId();

  // Get the text content for filtering
  const textContent = React.useMemo(() => {
    if (value) {
      return value;
    }
    if (typeof children === 'string') {
      return children;
    }
    return '';
  }, [children, value]);

  // Check if item should be visible based on search
  const isVisible = React.useMemo(() => {
    if (!search) {
      return true;
    }
    return textContent.toLowerCase().includes(search.toLowerCase());
  }, [search, textContent]);

  // Register/unregister item
  React.useEffect(() => {
    if (itemRef.current && isVisible) {
      registerItem(itemId, itemRef.current, disabled);
    }
    return () => unregisterItem(itemId);
  }, [itemId, registerItem, unregisterItem, disabled, isVisible]);

  // Handle selection state
  const itemIndex = items.findIndex((item) => item.id === itemId);
  const isSelected = itemIndex === selectedIndex && itemIndex >= 0;

  const handleClick = () => {
    if (disabled) {
      return;
    }
    onSelect?.(value || textContent);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      ref={itemRef}
      data-slot="command-item"
      data-selected={isSelected}
      data-disabled={disabled}
      className={cn(
        "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {children}
    </div>
  );
}

function CommandShortcut({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        'text-muted-foreground ml-auto text-xs tracking-widest',
        className
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
